import { FC, memo } from 'react';
import { Box, SimpleGrid, Text } from '@chakra-ui/react';

import { OptionallyDisplayWhen } from '../OptionallyDisplayWhen';
import { DataSectionPresenterProps } from './types/DataSectionPresenterProps';
import { DataSectionPresenterItem } from './DataSectionPresenterItem';

const DataSectionPresenterComponent: FC<DataSectionPresenterProps> = ({
  children,
  data,
  displayWhen,
  heading,
  workflowDataResolverDependencies,
  repeatFor,
  parentContext,
}) => {
  if (repeatFor && parentContext) {
    const repeatedItems = parentContext[repeatFor];

    if (!Array.isArray(repeatedItems) || repeatedItems.length === 0) {
      return (
        <OptionallyDisplayWhen
          evaluatable={displayWhen}
          workflowDataResolverDependencies={workflowDataResolverDependencies}
        >
          <Box>
            {heading && (
              <Box>
                <Text fontSize="lg" fontWeight="medium" mb={6}>
                  {heading}
                </Text>
              </Box>
            )}
            <SimpleGrid columns={1} gap={6}>
              <Text color="fg.subtle">None added</Text>
            </SimpleGrid>
          </Box>
          {children}
        </OptionallyDisplayWhen>
      );
    }

    return (
      <OptionallyDisplayWhen
        evaluatable={displayWhen}
        workflowDataResolverDependencies={workflowDataResolverDependencies}
      >
        <SimpleGrid gap={10}>
          {repeatedItems.map((item: any, idx: number) => (
            <DataSectionPresenterComponent
              key={item.id}
              data={data}
              heading={heading && `${heading} #${idx + 1}`}
              parentContext={item}
              workflowDataResolverDependencies={{
                ...workflowDataResolverDependencies,
                documentPath: [
                  ...(workflowDataResolverDependencies?.documentPath || []),
                  repeatFor,
                  String(idx),
                ],
              }}
            />
          ))}
        </SimpleGrid>
        {children}
      </OptionallyDisplayWhen>
    );
  }

  return (
    <OptionallyDisplayWhen
      evaluatable={displayWhen}
      workflowDataResolverDependencies={workflowDataResolverDependencies}
    >
      <Box>
        {heading && (
          <Text fontSize="lg" fontWeight="medium" mb={6}>
            {heading}
          </Text>
        )}

        <SimpleGrid columns={2} gap={6}>
          {data.map(({ label, value, displayWhen: dataDisplayWhen }, i) => (
            <DataSectionPresenterItem
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              displayWhen={dataDisplayWhen}
              label={label}
              parentContext={parentContext}
              value={value}
              workflowDataResolverDependencies={
                workflowDataResolverDependencies
              }
            />
          ))}
        </SimpleGrid>
      </Box>
      {children}
    </OptionallyDisplayWhen>
  );
};

export const DataSectionPresenter = memo(DataSectionPresenterComponent);
